import z from "zod";
import type { PartnerAccountId } from "@remi/enums/partner-account-id";
import type { FinalFindingsAutomationDataType } from "@remi/types/fsd-automation";
import type { InstallScheduleSubmission, SunrunAccountId } from "@remi/types/puppeteer";
import type { PreParsedPartnerTaskRow } from "@remi/types/sunrun";

const sunrunAutomationErrorTypeValues = [
  "UNKNOWN",
  "PRE_PTO_ERROR",
  "GUT_CHECK_WO_SCHEDULING",
  "GOOGLE_ADDRESS_VALIDATION_ERROR",
  "WO_PROCESSING_CSV_PARSE_ERROR",
  "WO_PROCESSING_PARSE_ERROR",
  "WO_PROCESSING_CREATION_ERROR",
  "FSD_INGESTION_ERROR",
  "TASK_ATTEMPT_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "PROJECT_SCHEDULE_DATE_NOT_FOUND",
  "WORK_ORDER_NOT_FOUND",
  "WORK_ORDER_SUNRUN_RECORD_ID_NOT_FOUND",
  "WORK_ORDER_STATUS_NOT_FOUND",
  "WORK_ORDER_TYPE_NOT_FOUND",
  "PARTNER_TASK_INGESTION_FAILURE",
  "PARTNER_TASK_SUNRUN_RECORD_ID_NOT_FOUND",
  "FAILED_TO_SET_INVOICE_NUMBER",
  "FAILED_TO_SET_INVOICE_AMOUNT",
  "FAILED_TO_DOWNLOAD_INVOICE_PDF",
  "CANNOT_EXTRACT_SALESFORCE_RECORD_ID_FOR_ATTACHMENT",
  "FSD_FINAL_FINDINGS_PARTIALLY_SYNCED",
  "FSD_FINAL_FINDINGS_EDIT_MODE_FAILED",
  "FSD_FINAL_FINDINGS_FIELD_NOT_FOUND",
  "AUTOMATION_DATA_NOT_FOUND",
  "BID_REQUEST_NO_EXTERNAL_URL",
  "BID_NOT_FOUND",
  "NO_FSD_FOR_BID",
  "BID_NOT_FOUND_FOR_FILE_INGESTION",
  "NO_EXTERNAL_URLS_FOUND_FOR_FILE_INGESTION",
  "NO_EXTERNAL_URLS_FOUND_FOR_FINAL_FINDINGS_SYNC",
  "ACCEPT_OR_DECLINE_DISPATCH_NO_EXTERNAL_URL",
  "BID_REQUEST_NO_EXTERNAL_URL_FOR_FILE_INGESTION",
  "JSON_PARSE_ERROR",
  "FAILED_SAVE_FSD_CHANGES",
  "FAILED_TO_UPLOAD_INVOICE_PDF",
  "GUT_CHECK_WO_RESCHEDULING",
  "GUT_CHECK_WO_SUBMIT",
  "ADDRESS_NOT_PROVIDED",
  "CONTACT_INFO_NOT_PROVIDED",
  "INVALID_FSD_STATUS",
  "UNRESOLVABLE_DISPATCH_STATE",
  "FSD_DISPATCH_NOT_FOUND",
  "FSD_DISPATCH_PAGE_NOT_LOADED",
  "FSD_DISPATCH_STATE_UNAVAILABLE",
  "FSD_DISPATCH_STATE_READ_FAILED",
  "ACCEPT_OR_DECLINE_DISPATCH_ERROR_PAGE_FOUND",
  "FSD_INGESTION_ERROR_PAGE_FOUND",
  "ACCEPT_DISPATCH_ENTER_EDIT_MODE_FAILED",
  "ACCEPT_DISPATCH_SET_CHECKBOX_FAILED",
  "DECLINE_DISPATCH_ENTER_EDIT_MODE_FAILED",
  "DECLINE_DISPATCH_SET_CHECKBOX_FAILED",
  "DECLINE_DISPATCH_SET_REASON_FAILED",
  "SCHEDULE_WORK_ORDER_DID_NOT_COMPLETE",
] as const;
const sunrunAutomationErrorTypeSchema = z.enum(sunrunAutomationErrorTypeValues);
export const SunrunAutomationErrorType = sunrunAutomationErrorTypeSchema.enum;
type SunrunAutomationErrorType = z.infer<typeof sunrunAutomationErrorTypeSchema>;

export type SunrunAutomationErrors =
  | { errorType: typeof SunrunAutomationErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof SunrunAutomationErrorType.PRE_PTO_ERROR; data: { automationId: string } }
  | {
      errorType: typeof SunrunAutomationErrorType.GUT_CHECK_WO_SCHEDULING;
      data: {
        workOrderData: {
          workOrder: string;
          projectId: number;
          accountId?: PartnerAccountId;
          bidId?: number;
        }[];
        checkType: string;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.GOOGLE_ADDRESS_VALIDATION_ERROR;
      data: { address?: string; entityNumber?: string; status?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WO_PROCESSING_CSV_PARSE_ERROR;
      data: { workOrderNumbers: string[]; ingestionTypeLabel: string; accountId: SunrunAccountId };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WO_PROCESSING_PARSE_ERROR;
      data: { workOrderNumbers: string[]; ingestionTypeLabel: string; accountId: SunrunAccountId };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WO_PROCESSING_CREATION_ERROR;
      data: { workOrderNumbers: string[]; ingestionTypeLabel: string; accountId: SunrunAccountId };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_INGESTION_ERROR;
      data: {
        failedDispatches: { url: string; error: string; errorType?: string }[];
        accountId: SunrunAccountId;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.TASK_ATTEMPT_NOT_FOUND;
      data: { taskAttemptId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.PROJECT_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.PROJECT_SCHEDULE_DATE_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WORK_ORDER_NOT_FOUND;
      data: { workOrderId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WORK_ORDER_SUNRUN_RECORD_ID_NOT_FOUND;
      data: { workOrderId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WORK_ORDER_STATUS_NOT_FOUND;
      data: { workOrderUrl: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.WORK_ORDER_TYPE_NOT_FOUND;
      data: { workOrderId: string; sunrunRecordId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.PARTNER_TASK_INGESTION_FAILURE;
      data: {
        parseFailures: PreParsedPartnerTaskRow[];
        failedURLs: string[];
        bidCreationFailures: number;
        bidRequestParseFailures: string[];
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.PARTNER_TASK_SUNRUN_RECORD_ID_NOT_FOUND;
      data: { partnerTaskNumber: string; url: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FAILED_TO_SET_INVOICE_NUMBER;
      data: { invoiceNumber: string | undefined };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FAILED_TO_SET_INVOICE_AMOUNT;
      data: { invoiceAmount: number };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FAILED_TO_DOWNLOAD_INVOICE_PDF;
      data: { fileId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.CANNOT_EXTRACT_SALESFORCE_RECORD_ID_FOR_ATTACHMENT;
      data: { fsdUrl: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_FINAL_FINDINGS_PARTIALLY_SYNCED;
      data: {
        url: string;
        data: FinalFindingsAutomationDataType;
        fileLength: number;
        isStatusSubmitted: boolean;
        isFinalFindingsPopulated: boolean;
        isAppointmentDatePopulated: boolean;
        isFsActionSubmitted: boolean;
        isFileUploaded: boolean;
        relatedTabRendered: boolean;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_FINAL_FINDINGS_EDIT_MODE_FAILED;
      // triggerAttempts is always present on this path. The diagnostics after it are
      // best-effort: a field is absent when its probe timed out or threw, and
      // diagnosticsError then names the probe that failed.
      data: {
        fsdUrl: string;
        currentUrl: string;
        step: string;
        triggerAttempts?: {
          selector: string;
          outcome: string;
          elapsedMs: number;
          errorName?: string;
          errorMessage?: string;
        }[];
        pageTitle?: string;
        editButtonCount?: number;
        layoutItemCount?: number;
        screenshotKey?: string;
        diagnosticsError?: string;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_FINAL_FINDINGS_FIELD_NOT_FOUND;
      data: { fsdUrl: string; currentUrl: string; step: string; value?: unknown };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.AUTOMATION_DATA_NOT_FOUND;
      data: { bidId: number; invoiceFsdId?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.BID_REQUEST_NO_EXTERNAL_URL;
      data: { bidId: number; fsdId?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.BID_NOT_FOUND;
      data: undefined | { projectId: number };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.NO_FSD_FOR_BID;
      data: { bidId: number; dispatchId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.BID_NOT_FOUND_FOR_FILE_INGESTION;
      data: { bidId: number };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.NO_EXTERNAL_URLS_FOUND_FOR_FILE_INGESTION;
      data: { bidId?: number; bidRequestId?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.NO_EXTERNAL_URLS_FOUND_FOR_FINAL_FINDINGS_SYNC;
      data: { fsdId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.ACCEPT_OR_DECLINE_DISPATCH_NO_EXTERNAL_URL;
      data: { fsdId: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.BID_REQUEST_NO_EXTERNAL_URL_FOR_FILE_INGESTION;
      data: { bidId: number };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.JSON_PARSE_ERROR;
      data: { json: unknown };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FAILED_SAVE_FSD_CHANGES;
      data: { pageUrl: string; errorImageUrl: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FAILED_TO_UPLOAD_INVOICE_PDF;
      data: { fileName: string; url: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.GUT_CHECK_WO_RESCHEDULING;
      data: {
        workOrderData: {
          workOrder: string;
          projectId: number;
          bidId: number;
          accountId: string;
          rescheduleSource: InstallScheduleSubmission | null;
        }[];
        checkType: string;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.GUT_CHECK_WO_SUBMIT;
      data: {
        workOrderData: {
          workOrder: string;
          projectId: number;
          bidId: number | null;
          taskId: string | null;
          accountId?: string;
        }[];
        checkType: string;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.ADDRESS_NOT_PROVIDED;
      data: { addressString: string | undefined };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.CONTACT_INFO_NOT_PROVIDED;
      data: { workOrderRecordDetail: unknown };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.INVALID_FSD_STATUS;
      data: {
        dispatchState: string;
        // Salesforce record id (a0QU…) and the human dispatch name (FSD-1294027) are different
        // identifier spaces; a caller has one or the other, never both.
        fsdRecordId?: string;
        fsDispatchName?: string;
        accountId: SunrunAccountId;
        automationId?: string;
      };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.UNRESOLVABLE_DISPATCH_STATE;
      data: { dispatchId: string; dispatchUrl: string; rawStateValue: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_DISPATCH_NOT_FOUND;
      data: { fsdId: string; accountId: SunrunAccountId; automationId?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_DISPATCH_PAGE_NOT_LOADED;
      data: { pageUrl: string; accountId: SunrunAccountId; automationId?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_DISPATCH_STATE_UNAVAILABLE;
      data: { fsdId: string; accountId: SunrunAccountId; automationId?: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_DISPATCH_STATE_READ_FAILED;
      data: { pageUrl: string; causeMessage: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.ACCEPT_OR_DECLINE_DISPATCH_ERROR_PAGE_FOUND;
      data: { dispatchUrl: string };
    }
  | {
      errorType: typeof SunrunAutomationErrorType.FSD_INGESTION_ERROR_PAGE_FOUND;
      data: { dispatchUrl: string; accountId: SunrunAccountId };
    }
  | { errorType: typeof SunrunAutomationErrorType.ACCEPT_DISPATCH_ENTER_EDIT_MODE_FAILED; data: undefined }
  | { errorType: typeof SunrunAutomationErrorType.ACCEPT_DISPATCH_SET_CHECKBOX_FAILED; data: undefined }
  | { errorType: typeof SunrunAutomationErrorType.DECLINE_DISPATCH_ENTER_EDIT_MODE_FAILED; data: undefined }
  | { errorType: typeof SunrunAutomationErrorType.DECLINE_DISPATCH_SET_CHECKBOX_FAILED; data: undefined }
  | { errorType: typeof SunrunAutomationErrorType.DECLINE_DISPATCH_SET_REASON_FAILED; data: { reason: string } }
  | {
      errorType: typeof SunrunAutomationErrorType.SCHEDULE_WORK_ORDER_DID_NOT_COMPLETE;
      data: { resultType: string; projectId: number; workOrderId: string };
    };

export const sunrunAutomationErrorDetails: Record<SunrunAutomationErrorType, { sentryDescription: string }> = {
  UNKNOWN: { sentryDescription: "Unknown Sunrun Automation Error" },
  PRE_PTO_ERROR: { sentryDescription: "Pre PTO Error" },
  GUT_CHECK_WO_SCHEDULING: {
    sentryDescription: "Gut Check Work Order Scheduling: There are WOs that should be scheduled in Salesforce.",
  },
  GOOGLE_ADDRESS_VALIDATION_ERROR: {
    sentryDescription: "Google Address Validation Error",
  },
  WO_PROCESSING_CSV_PARSE_ERROR: {
    sentryDescription: "Work Order Processing: CSV parse errors",
  },
  WO_PROCESSING_PARSE_ERROR: {
    sentryDescription: "Work Order Processing: Business logic parse errors",
  },
  WO_PROCESSING_CREATION_ERROR: {
    sentryDescription: "Work Order Processing: Creation errors (missing PT code)",
  },
  FSD_INGESTION_ERROR: {
    sentryDescription: "FSD Ingestion Error",
  },
  TASK_ATTEMPT_NOT_FOUND: {
    sentryDescription: "Task Attempt Not Found",
  },
  PROJECT_NOT_FOUND: {
    sentryDescription: "Project Not Found",
  },
  PROJECT_SCHEDULE_DATE_NOT_FOUND: {
    sentryDescription: "Project Schedule Date Not Found",
  },
  WORK_ORDER_NOT_FOUND: {
    sentryDescription: "Work Order Not Found",
  },
  WORK_ORDER_SUNRUN_RECORD_ID_NOT_FOUND: {
    sentryDescription: "Work Order Sunrun Record ID Not Found",
  },
  WORK_ORDER_STATUS_NOT_FOUND: {
    sentryDescription: "Work Order Status Not Found",
  },
  WORK_ORDER_TYPE_NOT_FOUND: {
    sentryDescription: "Work Order Type__c missing on Sunrun record — required to compute photo upload tags",
  },
  PARTNER_TASK_INGESTION_FAILURE: {
    sentryDescription: "Partner Task Ingestion Failure",
  },
  PARTNER_TASK_SUNRUN_RECORD_ID_NOT_FOUND: {
    sentryDescription: "Partner task scraped URL did not contain a Sunrun record id",
  },
  FAILED_TO_SET_INVOICE_NUMBER: {
    sentryDescription: "Failed to set invoice number",
  },
  FAILED_TO_SET_INVOICE_AMOUNT: {
    sentryDescription: "Failed to set invoice amount",
  },
  FAILED_TO_DOWNLOAD_INVOICE_PDF: {
    sentryDescription: "Failed to download invoice PDF",
  },
  CANNOT_EXTRACT_SALESFORCE_RECORD_ID_FOR_ATTACHMENT: {
    sentryDescription: "Cannot extract Salesforce record ID for attachment upload",
  },
  FSD_FINAL_FINDINGS_PARTIALLY_SYNCED: {
    sentryDescription: "FSD Final Findings Partially Synced",
  },
  FSD_FINAL_FINDINGS_EDIT_MODE_FAILED: {
    sentryDescription: "FSD Final Findings Edit Mode Failed",
  },
  FSD_FINAL_FINDINGS_FIELD_NOT_FOUND: {
    sentryDescription: "FSD Final Findings Field Not Found",
  },
  AUTOMATION_DATA_NOT_FOUND: {
    sentryDescription: "Automation Data Not Found For FSD",
  },
  BID_REQUEST_NO_EXTERNAL_URL: {
    sentryDescription: "Bid Request Has No External URL",
  },
  BID_NOT_FOUND: {
    sentryDescription: "Bid Not Found",
  },
  NO_FSD_FOR_BID: {
    sentryDescription: "No FSD for Bid",
  },
  BID_NOT_FOUND_FOR_FILE_INGESTION: {
    sentryDescription: "Bid Not Found For File Ingestion",
  },
  NO_EXTERNAL_URLS_FOUND_FOR_FILE_INGESTION: {
    sentryDescription: "No External URLs Found For File Ingestion",
  },
  NO_EXTERNAL_URLS_FOUND_FOR_FINAL_FINDINGS_SYNC: {
    sentryDescription: "No External URLs Found For FSD",
  },
  ACCEPT_OR_DECLINE_DISPATCH_NO_EXTERNAL_URL: {
    sentryDescription: "No External URLs Found For Accept Or Decline Dispatch",
  },
  BID_REQUEST_NO_EXTERNAL_URL_FOR_FILE_INGESTION: {
    sentryDescription: "Bid Request Has No External URL For File Ingestion",
  },
  JSON_PARSE_ERROR: {
    sentryDescription: "JSON Parse Error",
  },
  FAILED_SAVE_FSD_CHANGES: {
    sentryDescription: "Failed to save FSD changes",
  },
  FAILED_TO_UPLOAD_INVOICE_PDF: {
    sentryDescription: "Failed to upload invoice PDF",
  },
  GUT_CHECK_WO_RESCHEDULING: {
    sentryDescription: "Gut Check Work Order Rescheduling: There are WOs that need to be rescheduled in Salesforce.",
  },
  GUT_CHECK_WO_SUBMIT: {
    sentryDescription: "Gut Check Work Order Submit: There are WOs that should be submitted in Salesforce.",
  },
  ADDRESS_NOT_PROVIDED: {
    sentryDescription: "Address not provided in partner task data",
  },
  CONTACT_INFO_NOT_PROVIDED: {
    sentryDescription: "Contact information (firstName, lastName, or email) not provided in work order",
  },
  INVALID_FSD_STATUS: {
    sentryDescription: "Invalid FSD status value encountered during dispatch ingestion",
  },
  UNRESOLVABLE_DISPATCH_STATE: {
    sentryDescription: "Dispatch address state could not be resolved to a state code during ingestion",
  },
  FSD_DISPATCH_NOT_FOUND: {
    sentryDescription: "FSD dispatch not found: Sunrun API returned 404 for the record",
  },
  FSD_DISPATCH_PAGE_NOT_LOADED: {
    sentryDescription: "FSD dispatch state unreadable and the page URL contained no FSD record id",
  },
  FSD_DISPATCH_STATE_UNAVAILABLE: {
    sentryDescription: "FSD dispatch state unavailable from both the Sunrun API and the partners page",
  },
  FSD_DISPATCH_STATE_READ_FAILED: {
    sentryDescription: "FSD dispatch state read failed on partners page",
  },
  ACCEPT_OR_DECLINE_DISPATCH_ERROR_PAGE_FOUND: {
    sentryDescription: "Error page found when attempting to accept or decline dispatch",
  },
  FSD_INGESTION_ERROR_PAGE_FOUND: {
    sentryDescription: "Error page found during FSD ingestion",
  },
  ACCEPT_DISPATCH_ENTER_EDIT_MODE_FAILED: {
    sentryDescription: "Failed to enter edit mode for accept dispatch",
  },
  ACCEPT_DISPATCH_SET_CHECKBOX_FAILED: {
    sentryDescription: "Failed to set accept dispatch checkbox",
  },
  DECLINE_DISPATCH_ENTER_EDIT_MODE_FAILED: {
    sentryDescription: "Failed to enter edit mode for decline dispatch",
  },
  DECLINE_DISPATCH_SET_CHECKBOX_FAILED: {
    sentryDescription: "Failed to set decline dispatch checkbox",
  },
  DECLINE_DISPATCH_SET_REASON_FAILED: {
    sentryDescription: "Failed to set decline reason",
  },
  SCHEDULE_WORK_ORDER_DID_NOT_COMPLETE: {
    sentryDescription: "Scheduling the work order did not complete before invoice submission",
  },
};
